import { HttpClient } from '@angular/common/http';
import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from "@angular/router";
import { CmProAuthService } from '@app/core/services';
import { UseCPLoader, ICPLoader } from '@app/cp/shared/decorators/cploader/cploader-decorator';
import { UsersBus } from '@app/cp/shared/models/UsersBus.model';
import { AuthenticationService } from '@app/cp/shared/services/authentication.service';
import { GlobalsService } from '@app/cp/shared/services/globals.service';
import { environment } from '@env/environment';
import { AppClaims } from "@app/cp/shared/models/AppClaims";
import { CommonFunctions } from "@app/cp/shared/static/common-functions";
import { LogoutComponent } from "@app/shared/user/logout/logout.component";
import { FullScreenComponent } from "@app/shared/layout/header/full-screen/full-screen.component";
import { CommonValues } from '@app/cp/shared/static/CommonValues';
import { DQOIReferenceDataQuery } from '@app/cp/dqoi/state/dqoi-reference-data.query';
import { DispatchesService } from '@app/cp/shared/services/dispatches.service';
import { nameOf } from '@app/shared/utils/helper';

@UseCPLoader()
@Component({
    selector: 'sa-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss']
})
export class HeaderComponent implements OnInit, AfterViewInit, OnDestroy {

    @ViewChild(LogoutComponent) logoutComponent: LogoutComponent;
    @ViewChild(FullScreenComponent) fullScreenComponent: FullScreenComponent;
    compLoader: ICPLoader = this as any;
    protected _http: any;
    envReleaseVers = environment.releaseVersion;
    public userName: string;
    public permissions: AppClaims;
    public loggedInUser: boolean = false;
    public showProfileCard: boolean = false;
    public toggledata: boolean = false;
    private fullScreenToggle: boolean = false;
    protected fullScreenButtonText: string = 'Fullscreen';
    private observer!: MutationObserver;
    private user: UsersBus;
    showAccessLoader: boolean;
    showLocalLoader: boolean;
    showServerLoader: boolean;


    constructor(private router: Router, _http: HttpClient,
        private globals: GlobalsService,
        private authenticationService: AuthenticationService,
        private authService: CmProAuthService,
        private qry_rd: DQOIReferenceDataQuery,
        private dispatchService: DispatchesService,
        private elementRef: ElementRef,) {
        this._http = _http;
        this.compLoader.cpLoaderSize = 'sm';
    }

    ngOnDestroy() {
        this.authService.unsubscribeToLoginEvents();
    }

    ngOnInit() {

        this.authService.subscribeToLoginEvents((user: UsersBus) => {
            if (user) {
                this.user = user;
                this.userName = user.username;
                this.permissions = user.appClaims;
                this.loggedInUser = true;
            } else {
                this.userName = null;
                this.loggedInUser = false;
            }
        });

    }

    hasServerRefDataRefreshRole() {
        return this.user.hasRole(nameOf<AppClaims>().serverReferenceDataRefresh);
    }

    refreshLocalReferenceData() {
        this.compLoader.cpLoader = true;
        this.showLocalLoader = true;
        this.qry_rd.ensureRefDataIsLoaded(this.user.companyId, true).subscribe({
            next: () => {
                this.compLoader.cpLoader = false;
                this.showLocalLoader = false;
                this.globals.notifySuccess("Local reference data refreshed");
            },
            error: (err) => {
                console.error(err);
                this.compLoader.cpLoader = false;
                this.showLocalLoader = false;
                this.globals.notifyError(err.message, true);
            }
        });
    }

    refreshServerReferenceData() {
        this.compLoader.cpLoader = true;
        this.showServerLoader = true;
        this.dispatchService.GetReferenceData(this.user.companyId, true).subscribe({
            next: () => {
                this.compLoader.cpLoader = false;
                this.showServerLoader = false;
                this.globals.notifySuccess("Server reference data refreshed");
            },
            error: (err) => {
                console.error(err);
                this.compLoader.cpLoader = false;
                this.showServerLoader = false;
                this.globals.notifyError(err.message, true);
            }
        });
    }

    ngAfterViewInit() {
        this.observer = new MutationObserver(() => {
            this.updateFullscreenText();
        });
        this.observer.observe(document.body, { childList: true, subtree: true });
    }

    showCard() {
        this.showProfileCard = !this.showProfileCard;
        this.toggledata = false;
    }

    refreshProfile() {
        this.compLoader.cpLoader = true;
        this.showAccessLoader = true;
        this.authenticationService.refreshProfile(this.authService.accountInfo.username).subscribe({
            next: (user: UsersBus) => {
                const usr = new UsersBus(user);
                if (usr.errorMessage === "User Not Found") {
                    //NOTE: dont let anything render till we have the user info
                    this.authService.logout(undefined, false).subscribe(() => {
                        this.globals.notifyError(CommonValues.ErrorMessages_AccessDenied, true);
                        this.router.navigate(['/login']);
                    });
                } else {
                    this.authService.parseUserResponse(usr, true, false);
                    this.globals.notifySuccess("User access permissions refreshed successfully.");
                    this.compLoader.cpLoader = false;
                    this.showAccessLoader = false;
                }
            },
            error: (e) => {
                console.error(e);
                this.compLoader.cpLoader = false;
                this.globals.notifyError("Unable to refresh user access. Please try again. If problem persists please contact your administrator for access.", true);
                this.showAccessLoader = false;
            }
        });
    }

    protected readonly environment = environment;

    showPermissions() {
        this.toggledata = !this.toggledata;
        this.copyPermissions();
    }

    copyPermissions() {
        CommonFunctions.copyToClipboard(JSON.stringify(this.permissions));
        this.globals.notifySuccess(`Copied Permissions for to Clipboard`);
    }

    logout() {
        this.logoutComponent.showPopup();
    }

    fullscreen() {
        this.fullScreenToggle = !this.fullScreenToggle;
        this.fullScreenComponent.onToggle();
    }

    private updateFullscreenText() {
        const classExists = document.querySelector('.full-screen') !== null;
        this.fullScreenButtonText = classExists ? 'Exit Fullscreen' : 'Fullscreen';
    }

    @HostListener('document:click', ['$event'])
    onClickOutside(event: Event) {
        if (!this.elementRef.nativeElement.contains(event.target)) {
            this.showProfileCard = false;
        }
    }
}
